/**
 * ShapeLibraryPanel — bibliothèque parcourable de 370 formes SVG.
 * Colonne verticale de catégories (comme le Studio I.A) + grille + recherche
 * + favoris.
 *
 * Cet en-tête annonçait « ~80 SVG shapes » : le compte datait des premières
 * versions et n'avait pas suivi les ajouts. `data/shapeLibrary.js` en contient
 * 370, réparties sur 18 catégories — plus de quatre fois le chiffre annoncé, de
 * quoi croire à tort que la bibliothèque était minuscule.
 */
import { useMemo } from 'react';
import { sanitizeSvgFragment } from '../utils/svgSanitize';
import { shapes, SHAPE_CATEGORIES } from '../data/shapeLibrary';
import { useShapeLibStore } from '../store/useShapeLibStore';

export default function ShapeLibraryPanel({ onAddSvgShape, onClose }) {
  const { favorites, searchQuery, activeCategory, toggleFavorite, isFavorite, setSearch, setCategory } = useShapeLibStore();

  const ALL_CATS = [{ id: 'all', name: 'Tout', icon: '◎' }, ...SHAPE_CATEGORIES];

  const filtered = useMemo(() => {
    let result = shapes;
    if (activeCategory === 'favorites') {
      result = result.filter(s => favorites.includes(s.id));
    } else if (activeCategory !== 'all') {
      result = result.filter(s => s.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.tags.some(t => t.includes(q)) ||
        s.category.includes(q)
      );
    }
    // Le bandeau Favoris affiche déjà les formes épinglées : sans cette
    // exclusion, chacune apparaissait DEUX fois, une fois en haut et une fois
    // à sa place dans la grille — on croyait avoir deux formes identiques.
    if (activeCategory !== 'favorites' && favorites.length > 0) {
      result = result.filter(s => !favorites.includes(s.id));
    }
    return result;
  }, [activeCategory, searchQuery, favorites]);

  const favShapes = useMemo(() => {
    if (activeCategory === 'favorites' || favorites.length === 0) return [];
    return shapes.filter(s => favorites.includes(s.id));
  }, [favorites, activeCategory]);

  const activeCat = ALL_CATS.find(c => c.id === activeCategory) || ALL_CATS[0];

  return (
    <div className="flex h-full">
      {/* ── Vertical category sidebar ── */}
      <div className="w-[48px] shrink-0 bg-black/20 border-r border-[rgba(var(--forge-border),0.2)] flex flex-col items-center py-3 gap-1">
        {/* Header icon */}
        <div className="mb-3 pb-3 border-b border-[rgba(var(--forge-border),0.2)] w-full flex justify-center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(var(--forge-mauve),1)" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
        </div>

        {ALL_CATS.map(cat => {
          const isActive = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              title={cat.name}
              className={`w-[38px] h-[38px] rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all ${
                isActive
                  ? 'bg-[rgba(var(--forge-mauve),0.12)] text-[rgba(var(--forge-mauve),1)] shadow-[inset_0_0_12px_rgba(var(--forge-mauve),0.08)]'
                  : 'text-[rgba(var(--forge-muted),0.5)] hover:text-[rgba(var(--forge-text),0.8)] hover:bg-white/5'
              }`}
              style={isActive ? { borderLeft: '2px solid rgba(var(--forge-mauve), 0.6)' } : undefined}
            >
              <span className="text-[11px]">{cat.icon}</span>
              <span className="text-[5px] font-mono font-bold uppercase tracking-widest leading-none truncate max-w-[36px]">
                {cat.name.length > 6 ? cat.name.slice(0, 5) + '.' : cat.name}
              </span>
            </button>
          );
        })}

        {/* Close at bottom */}
        <div className="mt-auto pt-3 border-t border-[rgba(var(--forge-border),0.2)] w-full flex justify-center">
          <button onClick={onClose} title="Fermer" aria-label="Fermer" className="w-[32px] h-[32px] rounded-lg flex items-center justify-center text-[rgba(var(--forge-muted),0.4)] hover:text-forge-rose hover:bg-forge-rose/10 transition-all">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-white/5 bg-black/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-[9px] font-mono font-bold uppercase tracking-[0.2em] text-white/50">Formes</h2>
            <span className="text-[8px] font-mono text-[rgba(var(--forge-mauve),0.8)] uppercase tracking-widest">/ {activeCat.name}</span>
          </div>
          {/* Cette croix n'avait ni `title` ni `aria-label`, contrairement à sa
              jumelle du pied de la colonne de catégories : un lecteur d'écran
              n'annonçait que « bouton », et rien n'apparaissait au survol. */}
          <button onClick={onClose} title="Fermer la bibliothèque de formes" aria-label="Fermer la bibliothèque de formes" className="w-[24px] h-[24px] rounded-md flex items-center justify-center bg-black/20 border border-white/10 text-[rgba(var(--forge-muted),0.6)] hover:text-forge-rose hover:bg-forge-rose/15 hover:border-forge-rose/25 transition-all">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b border-white/5">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une forme..."
            className="w-full bg-black/30 border border-white/5 rounded-lg px-3 py-1.5 text-[9px] font-mono text-forge-text placeholder-forge-muted/30 outline-none focus:border-forge-mauve/30 shadow-inner transition-colors"
          />
        </div>

        {/* Scrollable grid */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
          {/* Favorites section */}
          {favShapes.length > 0 && (
            <div className="pb-3 mb-3 border-b border-forge-border/10">
              <p className="text-[8px] text-forge-rose/60 font-mono font-bold uppercase tracking-widest mb-2 flex items-center gap-1">
                <span>♥</span> Favoris
              </p>
              <div className="grid grid-cols-4 gap-1.5">
                {favShapes.map(shape => (
                  <ShapeItem key={`fav-${shape.id}`} shape={shape} onAdd={onAddSvgShape} isFav={true} onToggleFav={toggleFavorite} />
                ))}
              </div>
            </div>
          )}

          {/* Main grid */}
          {filtered.length === 0 && favShapes.length === 0 ? (
            <p className="text-[9px] text-forge-muted/30 font-mono text-center py-8 uppercase tracking-widest">
              {searchQuery ? 'Aucun resultat' : 'Aucun favori'}
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-1.5">
              {filtered.map(shape => (
                <ShapeItem key={shape.id} shape={shape} onAdd={onAddSvgShape} isFav={isFavorite(shape.id)} onToggleFav={toggleFavorite} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ShapeItem({ shape, onAdd, isFav, onToggleFav }) {
  return (
    <div className="relative group">
      <button
        onClick={() => onAdd(shape)}
        className="w-full aspect-square bg-black/30 border border-white/5 rounded-lg flex items-center justify-center hover:bg-forge-mauve/10 hover:border-forge-mauve/20 transition-all shadow-inner group/btn"
        title={shape.name} aria-label={shape.name}
      >
        {shape.multiColor && shape.svgContent ? (
          <svg
            viewBox={shape.viewBox}
            className="w-[70%] h-[70%]"
            // Une variante pour FOND SOMBRE (Triman blanc, −18 en rond) : sa vignette sur un fond sombre fixe, sinon le blanc
            // disparaît en thème clair. La vignette seule : le symbole posé reste transparent.
            style={shape.variante_de ? { backgroundColor: '#14161A', borderRadius: 4 } : undefined}
            // Wrap+unwrap pour préserver les tags SVG (cf. svgSanitize.js).
            dangerouslySetInnerHTML={{ __html: sanitizeSvgFragment(shape.svgContent) }}
          />
        ) : (
          <svg
            viewBox={shape.viewBox}
            className="w-[55%] h-[55%] text-forge-text/60 group-hover/btn:text-forge-mauve transition-colors"
            fill={shape.fill ? 'currentColor' : 'none'}
            stroke={shape.fill ? 'none' : 'currentColor'}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d={shape.path} />
          </svg>
        )}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onToggleFav(shape.id); }}
        className={`absolute top-0.5 right-0.5 w-4 h-4 rounded flex items-center justify-center text-[8px] transition-all ${
          isFav
            ? 'text-forge-rose opacity-100'
            : 'text-forge-muted/20 opacity-0 group-hover:opacity-100 hover:text-forge-rose'
        }`}
      >
        {isFav ? '♥' : '♡'}
      </button>
    </div>
  );
}
